import { sortBy } from 'lodash-es';
import axios from '@/axios';

const helpers = {
    formatArticle(article)
    {
        if(article.photo_id !== null)
        {
            article.photo_id = parseInt(article.photo_id) || null;
        }
    },
};

export default
{
    namespaced: true,


    // ------------------------------------------------------------------------- STATE

    state()
    {
        return {
            all:      [],    // All articles
            cArticle: null,  // Currently displayed article
        };
    },


    // ------------------------------------------------------------------------- GETTERS

    getters:
    {
        sortedArticles: state => sortBy(state.all, ['status', 'label']),

        oneById: state => id => state.all.find(a => a.id == id),
    },


    // ------------------------------------------------------------------------- MUTATIONS

    mutations:
    {
        setAll(state, articles)
        {
            for(const article of articles)
            {
                helpers.formatArticle(article);
            }

            state.all = articles;
        },

        /**
         * Add an article to articles list.
         * @param {Object} state
         * @param {Object} article
         */
        addToArticlesList(state, article)
        {
            helpers.formatArticle(article);
            state.all.push(article);
        },

        /**
         * Update an article in articles list.
         * @param {Object} state
         * @param {Object} article
         */
        updateInArticlesList(state, article)
        {
            const index = state.all.findIndex(a => a.id == article.id);
            if(index === -1)
            {
                throw new Error('Article not found');
            }

            helpers.formatArticle(article);
            state.all.splice(index, 1, article);
        },

        /**
         * Set current article.
         * @param {Object} state
         * @param {Object} article
         */
        setArticle(state, article)
        {
            state.cArticle = article;
        },
    },


    // ------------------------------------------------------------------------- ACTIONS

    actions:
    {
        /**
         * Initialize this store module.
         * @see `initializationActions` in src/store/index.js.
         */
        initialize({ commit, getters })
        {
        },

        /**
         * Retrieve the list of articles and save it.
         * @returns {Object[]}
         */
        async fetchAll({ commit }, options = {})
        {
            let url = '/api/article';
            if(options.withTags)
            {
                url += '/with-tags';
            }

            const { data } = await axios.get(url);
            commit('setAll', data);
            return data;
        },

        async fetchArticle({ commit, getters }, articleId)
        {
            const response = await axios.get(`/api/article/${articleId}`);
            const article = response.data;

            // Insert or update article in the list
            const mutation = getters.oneById(article.id)
                ? 'updateInArticlesList'
                : 'addToArticlesList';
            commit(mutation, article);

            return article;
        },

        async addToArticlesList({ commit }, article)
        {
            commit('addToArticlesList', article);
            return article;
        },

        async updateInArticlesList({ commit }, article)
        {
            commit('updateInArticlesList', article);
            return article;
        },

        async setCurrentArticle({ commit }, article)
        {
            commit('setArticle', article);
            return article;
        },

        /**
         * Insert a new article via ajax and reflect it to the store.
         *
         * @param {Object} article
         * @returns {Object}
         */
        async create({ commit }, article)
        {
            const { data } = await axios.post('/api/article', article);
            commit('addToArticlesList', data);
            return data;
        },

        /**
         * Update an existing article via ajax and reflect it to the store.
         *
         * @param {Object} article
         * @returns {Object}
         */
        async update({ commit }, article)
        {
            const id = article.id;
            const { data } = await axios.put(`/api/article/${id}`, article);
            commit('updateInArticlesList', data);
            return data;
        },

        /**
         * Duplicate an existing article via ajax and reflect it to the store.
         *
         * @param {Object} arg0
         * @param {{id: number, code: string, label?: ?string}} article
         * @returns {Object}
         */
        async duplicate({ commit }, { id, code, label = null } = {})
        {
            const url = `/api/article/${id}/duplicate`;
            const { data: copy } = await axios.post(url, { code, label });
            commit('addToArticlesList', copy);
            return copy;
        },
    },
};
